import { useEffect, useState } from 'react'

const INPUT_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'url', label: 'URL' },
  { value: 'secret', label: 'Password' },
  { value: 'expiry', label: 'Expiry' },
  { value: 'due_date', label: 'Due date' },
]

let fieldUid = 0

export function blankField() {
  fieldUid += 1
  return { _id: fieldUid, key: '', label: '', input_type: 'text', wide: false }
}

export function fieldsFromColumns(columns) {
  if (!columns?.length) return [blankField()]
  return columns.map((c) => {
    fieldUid += 1
    return {
      _id: fieldUid,
      key: c.key || '',
      label: c.label || c.key || '',
      input_type: c.input_type || 'text',
      wide: Boolean(c.wide),
    }
  })
}

export function toApiFields(cols) {
  return cols
    .filter((c) => String(c.label || '').trim())
    .map((c) => ({
      label: String(c.label).trim(),
      key: c.key || undefined,
      input_type: c.input_type || 'text',
      wide: Boolean(c.wide),
    }))
}

export function FieldEditor({ columns, onChange, disabled }) {
  function update(id, patch) {
    onChange(columns.map((c) => (c._id === id ? { ...c, ...patch } : c)))
  }

  function remove(id) {
    if (columns.length <= 1) return
    onChange(columns.filter((c) => c._id !== id))
  }

  function move(id, dir) {
    const idx = columns.findIndex((c) => c._id === id)
    const target = idx + dir
    if (idx < 0 || target < 0 || target >= columns.length) return
    const next = [...columns]
    const [item] = next.splice(idx, 1)
    next.splice(target, 0, item)
    onChange(next)
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-brand-muted">
        Each field becomes a column across the table (left to right). Types: Text, URL,
        Password, Expiry or Due date (both dates appear on ToDo / Expiring when due
        soon). Tick{' '}
        <span className="font-medium text-brand">Wide</span> for a large notes box with bold /
        underline while typing (ToDo tasks).
      </p>
      {columns.map((col, index) => (
        <div
          key={col._id}
          className="flex flex-col gap-2 rounded border border-slate-200 bg-white p-2 sm:flex-row sm:items-center"
        >
          <label className="min-w-0 flex-1 text-xs">
            <span className="text-brand-muted">Name</span>
            <input
              value={col.label}
              onChange={(e) => update(col._id, { label: e.target.value })}
              placeholder="e.g. Printer"
              disabled={disabled}
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs sm:w-36">
            <span className="text-brand-muted">Type</span>
            <select
              value={col.input_type}
              onChange={(e) => update(col._id, { input_type: e.target.value })}
              disabled={disabled}
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
            >
              {INPUT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label
            className="flex items-center gap-1 self-end pb-2 whitespace-nowrap text-xs text-brand-muted"
            title="Multi-line paragraph cell when editing"
          >
            <input
              type="checkbox"
              checked={col.wide}
              onChange={(e) => update(col._id, { wide: e.target.checked })}
              disabled={disabled || col.input_type !== 'text'}
            />
            Wide
          </label>
          <div className="flex gap-1 self-end pb-1.5">
            <button
              type="button"
              disabled={disabled || index === 0}
              onClick={() => move(col._id, -1)}
              className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40"
            >
              Up
            </button>
            <button
              type="button"
              disabled={disabled || index === columns.length - 1}
              onClick={() => move(col._id, 1)}
              className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40"
            >
              Down
            </button>
            <button
              type="button"
              disabled={disabled || columns.length <= 1}
              onClick={() => remove(col._id)}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-red-700 disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange([...columns, blankField()])}
        className="text-sm font-medium text-brand hover:underline"
      >
        Add field
      </button>
    </div>
  )
}

/** Compact fields editor with save/cancel for use on a data table. */
export function TableFieldsPanel({
  columns,
  disabled,
  onSave,
  onCancel,
  onDraftChange,
}) {
  const [draft, setDraft] = useState(() => fieldsFromColumns(columns))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  function handleDraftChange(next) {
    setDraft(next)
    onDraftChange?.(toApiFields(next))
  }

  async function handleSave() {
    const apiFields = toApiFields(draft)
    if (apiFields.length === 0) {
      setError('Add at least one field name.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await onSave(apiFields)
    } catch (err) {
      setError(
        typeof err?.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save fields.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <FieldEditor
        columns={draft}
        onChange={handleDraftChange}
        disabled={disabled || busy}
      />
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={handleSave}
          className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save fields'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="text-xs text-brand-muted underline"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * Field editor that keeps stable row ids in local draft state.
 * Use this when parent state is API columns (no `_id`) — calling
 * fieldsFromColumns() on every render remounts inputs and steals focus.
 *
 * @param {string|number} resetKey — change when switching table/block to reload draft
 * @param {array} columns — API-shaped columns from parent
 * @param {(apiColumns: array) => void} onChange — receives toApiFields(draft)
 */
export function StableFieldEditor({ resetKey, columns, onChange, disabled }) {
  const [draft, setDraft] = useState(() =>
    columns?.length ? fieldsFromColumns(columns) : [blankField()],
  )

  useEffect(() => {
    setDraft(columns?.length ? fieldsFromColumns(columns) : [blankField()])
    // Only remount draft when the block/table identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  function handleChange(next) {
    setDraft(next)
    onChange?.(toApiFields(next))
  }

  return <FieldEditor columns={draft} onChange={handleChange} disabled={disabled} />
}
